// ContentSections.tsx
import { Fragment, useMemo } from 'react';
import { SxProps, Typography } from '@mui/material';
import PageContainer from '../productPage/tableComponents/PageContainer';

type SectionArrayItem = {
    subTitle?: string;
    texts: string[];
};

type SectionsProp = SectionArrayItem[] | Map<string, string[]>;

export interface ContentSectionsProps {
    /** Required page-level title */
    title: string;

    /** Variable number of subsections; supports Array or Map<string, string[]> */
    sections?: SectionsProp;

    /** Container styling overrides (限制為物件形態) */
    containerSx?: SxProps;
    innerSx?: SxProps;

    /** Typography style overrides (限制為物件形態) */
    titleSx?: SxProps;
    subTitleSx?: SxProps;
    textSx?: SxProps;
    containerName?: string;
}

/** Default styles */
const defaultTitleSx = {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '100%',
    letterSpacing: '0em',
};

const defaultSubTitleSx = {
    fontFamily: 'Poppins',
    fontWeight: 500,
    fontSize: '18px',
    lineHeight: '100%',
    letterSpacing: '0em',
    color: '#B75332',
};

const defaultTextSx = {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: '16px',
    lineHeight: '1.3rem',
    letterSpacing: '0em',
};

function normalizeSections(sections?: SectionsProp): SectionArrayItem[] {
    if (!sections) return [];
    if (sections instanceof Map) {
        return Array.from(sections.entries()).map(([subTitle, texts]) => ({
            subTitle,
            texts: texts ?? [],
        }));
    }
    return sections.map((s) => ({
        subTitle: s.subTitle,
        texts: s.texts ?? [],
    }));
}

export default function ContentSections({
    title,
    sections,
    containerSx,
    innerSx,
    titleSx,
    subTitleSx,
    textSx,
    containerName,
}: ContentSectionsProps) {
    const items = useMemo(() => normalizeSections(sections), [sections]);

    return (
        <PageContainer
            containerName={containerName}
            sx={{
                paddingTop: '3rem',
                paddingBottom: '2rem',
                backgroundColor: 'white',
                ...(containerSx || {}),
            }}
            innerSx={{
                display: 'flex',
                flexDirection: 'column',
                rowGap: '24px',
                ...(innerSx || {}),
            }}
        >
            <Typography sx={{ ...defaultTitleSx, ...(titleSx || {}) }}>{title}</Typography>

            {items.map((sec, idx) => (
                <Fragment key={idx}>
                    {sec.subTitle && (
                        <Typography sx={{ ...defaultSubTitleSx, ...(subTitleSx || {}) }}>
                            {sec.subTitle}
                        </Typography>
                    )}
                    {sec.texts.map(
                        (t, i) =>
                            t?.trim() && (
                                <Typography key={i} sx={{ ...defaultTextSx, ...(textSx || {}) }}>
                                    {t}
                                </Typography>
                            )
                    )}
                </Fragment>
            ))}
        </PageContainer>
    );
}
